import { Component, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { PendingChanges } from '@guards/pending-changes.guard';
import { RecipientSuggestion } from '@components/select-recipient/select-recipient.component';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AsyncMergeJobObject,
  BuildingItemDto,
  BuildingService,
  EmailDetailDto,
  InspectionDetailDto,
  InspectionEntryMergeDto,
  InspectionGuidelinesDetailDto,
  InspectionGuidelinesService,
  InspectionMergeDto,
  InspectionPdfService,
  InspectionService,
  UserDetailDto,
  UserService,
} from '@artemis-software/wr-api';
import { BehaviorSubject, catchError, combineLatest, firstValueFrom, map, of, Subject, switchMap, takeUntil, } from 'rxjs';
import { TimeUtils } from '@shared/form-controls/time-picker/time-picker.component';
import { defaultPdfOptions } from '@/models/inspection-pdf-options';
import { NbDialogService, NbIconConfig, NbTabComponent, NbToastrService } from '@nebular/theme';
import { ConfirmDialogComponent } from '@components/dialog/confirm-dialog/confirm-dialog.component';
import {
  PreviousInspectionDialogComponent,
} from '@components/dialog/previous-inspection-dialog/previous-inspection-dialog.component';
import { InspectionFormWrapper } from './inspection-form';
import { createInspectionForm, } from './inspection-util';
import { AuthService } from '@services/auth.service';
import { InspectionEntriesComponent } from './inspection-entries/inspection-entries.component';
import { DetailFormComponent } from './detail-form/detail-form.component';
import { ConfirmEditDialogComponent } from '@components/dialog/confirm-edit-dialog/confirm-edit-dialog.component';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { tryWithLoading } from '@/utils/async-utils';
import { Store } from '@ngxs/store';
import { LoadAddresses } from '@/store/address/address.actions';
import {
  archive_tab,
  building_equipments_tab,
  entries_tab,
  general_tab,
  meta_data_tab,
  notes_tab,
  pdfs_tab,
  repair_tasks_tab,
} from '@pages/inspection-detail-page-v2/inspection-tab.constants';
import { Location } from '@angular/common';
import { SendEmailComponent } from '@components/dialog/send-email/send-email.component';
import { joinUrl } from '@/utils/url-utils';
import { InfoDialogComponent } from '@components/dialog/info-dialog/info-dialog.component';
import { InspectionContext } from '@pages/inspection-detail-page-v2/inspection.context';
import { InspectionNotesComponent } from '@pages/inspection-detail-page-v2/inspection-notes/inspection-notes.component';
import { userToIdReference } from '@/utils/id-reference';
import { currentTenantId, isInternalTenant } from '@/utils/admin-utils';
import EmailTypeEnum = EmailDetailDto.EmailTypeEnum;

const saveIcon: NbIconConfig = { icon: 'save-outline', pack: 'eva' };

const mergeStatusPollIntervalMs = 1000;
const mergeStatusPollTimeoutMs = 10 * 60 * 1000;
const maxConsecutivePollErrors = 3;
const maxConsecutiveUnknownPolls = 15;

@Component({
  standalone: false,
  selector: 'wr-inspection-detail-page-v2',
  templateUrl: './inspection-detail-page-v2.component.html',
  styleUrls: ['./inspection-detail-page-v2.component.scss'],
})
export class InspectionDetailPageV2Component implements PendingChanges, OnDestroy, OnInit {

  private readonly tenantId = currentTenantId();
  private readonly isInternalTenant = isInternalTenant();
  private readonly router = inject(Router);
  private readonly inspectionService = inject(InspectionService);
  private readonly inspectionPdfService = inject(InspectionPdfService);
  private readonly inspectionGuidelinesService = inject(InspectionGuidelinesService);
  private readonly nbToastrService = inject(NbToastrService);
  private readonly nbDialogService = inject(NbDialogService);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly buildingService = inject(BuildingService);
  private readonly store = inject(Store);
  private readonly location = inject(Location);
  private route = inject(ActivatedRoute);
  readonly inspectionContext = inject(InspectionContext);

  readonly clerkGuidelines = signal<InspectionGuidelinesDetailDto | null>(null);

  readonly inspectionEntriesComponent = viewChild(InspectionEntriesComponent);
  readonly detailFormComponent = viewChild(DetailFormComponent);
  readonly inspectionNotesComponent = viewChild(InspectionNotesComponent);
  readonly canEdit = signal(false);
  readonly submitting = signal(false);
  isNew = false;
  lockEdit = false;
  formWrapper: InspectionFormWrapper = new InspectionFormWrapper();
  inspection?: InspectionDetailDto;
  firstSubmit = false;
  private destroyed = false;
  destroy$ = new Subject<void>();
  saving$ = new BehaviorSubject(false);
  importing$ = new BehaviorSubject(false);
  fetching$ = new BehaviorSubject(false);
  deleting$ = new BehaviorSubject(false);
  loading$ = combineLatest([
    this.saving$,
    this.importing$,
    this.fetching$,
    this.deleting$,
  ]).pipe(map((booleans) => booleans.some((b) => b)));
  currentUser$ = new BehaviorSubject<UserDetailDto | undefined>(undefined);
  protected readonly general_tab = general_tab;
  protected readonly archive_tab = archive_tab;
  protected readonly entries_tab = entries_tab;
  protected readonly building_equipments_tab = building_equipments_tab;
  protected readonly pdfs_tab = pdfs_tab;
  protected readonly repair_tasks_tab = repair_tasks_tab;
  protected readonly meta_data_tab = meta_data_tab;
  protected readonly notes_tab = notes_tab;

  constructor() {
    this.route.paramMap.subscribe(async (params) => {
      const id = params.get('id');
      this.isNew = id === 'new';
      if (!id) {
        await this.router.navigate(['inspections']);
      } else {
        if (this.isNew) {
          this.canEdit.set(true);
          this.inspectionContext.inspectionId.set(undefined);
          await this.initForm(undefined);
          this.formWrapper.enable();
        } else {
          this.inspectionContext.inspectionId.set(id);
          this.inspectionContext.reloadInspectionPdfs();
          await tryWithLoading(this.fetching$, async () => {
            this.inspection = await firstValueFrom(this.inspectionService.findById(id));
            this.canEdit.set(this.inspection.permissions.canEdit);
            await this.initForm(this.inspection);
            if (this.isInternalTenant()) {
              await this.loadClerkGuidelines(id);
            }
          });
        }
      }
    });
  }

  get hasSendingStatus(): boolean {
    return this.inspection?.status == InspectionDetailDto.StatusEnum.Sent;
  }

  get willApplyPreviousInspection(): boolean {
    return !!this.formWrapper.form()?.controls.applyPreviousInspection?.value;
  }

  get hasAnyPendingAttachments(): boolean {
    const detailPending = this.detailFormComponent()?.hasAnyPendingAttachments ?? false;
    const entriesPending = this.inspectionEntriesComponent()?.hasAnyPendingAttachments ?? false;
    return detailPending || entriesPending;
  }

  async initForm(inspection?: InspectionDetailDto): Promise<void> {
    this.lockEdit = inspection?.status === InspectionDetailDto.StatusEnum.InProgress;

    this.store.dispatch(new LoadAddresses(inspection?.building.sections.map(s => s.addressId) ?? []));

    const form = createInspectionForm(inspection);

    form.updateValueAndValidity({
      emitEvent: false,
    });

    const applyLastInspection = async (building: BuildingItemDto | null | undefined): Promise<void> => {
      const tenantId = this.tenantId();
      if (inspection?.id || !building || !tenantId) {
        return;
      }
      const lastInspections = await firstValueFrom(this.inspectionService.findLastInspectionByBuilding({
        buildingId: building.id,
        tenantId: tenantId,
        searchText: '',
        inspectionType: form.controls.inspectionType?.value ?? InspectionDetailDto.InspectionTypeEnum.Regular,
      }));
      if (lastInspections.length > 0) {
        const lastInspection = lastInspections[0];
        form.controls.continuationNumber?.setValue(lastInspection.continuationNumber + 1);
        form.controls.normType?.setValue(lastInspection.normType);
        const takeLastInspection = await firstValueFrom(this.nbDialogService.open(PreviousInspectionDialogComponent, {
          context: {
            inspection: lastInspection,
          },
        }).onClose);
        if (takeLastInspection) {
          this.clearEntries();
          form.controls.applyPreviousInspection?.setValue(lastInspection.id);
          form.controls.usedTemplateId.setValue(lastInspection.usedTemplateId);
        } else {
          form.controls.applyPreviousInspection?.setValue(undefined);
        }
      } else {
        const isSchadstellenplan =
          form.controls.inspectionType?.value === InspectionDetailDto.InspectionTypeEnum.Schadstellenplan;
        const typeLabel = isSchadstellenplan ? 'Schadstellenplan' : 'Begehung';
        form.controls.continuationNumber?.setValue(1);
        // Der Schadstellenplan kennt keine eigene Norm - B1300 ist hier reiner Pflichtfeld-Platzhalter.
        form.controls.normType?.setValue(
          isSchadstellenplan ? InspectionDetailDto.NormTypeEnum.Norm1300 : undefined,
        );
        form.controls.applyPreviousInspection?.setValue(undefined);
        this.nbToastrService.info(
          `Für dieses Gebäude gibt es noch keinen Rundgang der Art „${typeLabel}“. Folgenummer startet bei 1.`,
          'Erster Rundgang dieser Art',
        );
      }
    };

    form.controls.building?.valueChanges.subscribe((building: BuildingItemDto | null | undefined) => applyLastInspection(building));

    form.controls.inspectionType?.valueChanges.subscribe(() => {
      if (inspection?.id) {
        return;
      }
      this.clearEntries();
      form.controls.applyPreviousInspection?.setValue(undefined);
      form.controls.usedTemplateId.setValue(undefined);
      applyLastInspection(form.controls.building?.value);
    });

    form.controls.status?.valueChanges.subscribe((status: InspectionDetailDto.StatusEnum) => {
      if (status === InspectionDetailDto.StatusEnum.Sent) {
        form.controls.sendingDate?.setValue(new Date());
      }
    });

    this.formWrapper.setForm(form);
    this.formWrapper.disable();
  }

  private async loadClerkGuidelines(inspectionId: string): Promise<void> {
    const guidelines = await firstValueFrom(
      this.inspectionGuidelinesService.findByInspectionAndType(inspectionId, 'CLERK').pipe(
        catchError(() => of(null)),
      ),
    );
    this.clerkGuidelines.set(guidelines ?? null);
  }

  clearEntries(): void {
    const formArray = this.formWrapper.controls()?.inspectionEntries;

    if (!formArray) {
      throw new Error('Inspection entries not found');
    }

    while (formArray.length > 0) {
      formArray.removeAt(0);
    }
  }

  async hasPendingChanges(): Promise<boolean> {
    // Der Notizen-Tab hängt nicht am formWrapper — sein Dirty-State muss den
    // Unsaved-Changes-Dialog trotzdem auslösen.
    return (this.formWrapper.enabled() && this.formWrapper.dirty())
      || (this.inspectionNotesComponent()?.noteControl.dirty ?? false);
  }

  async saveInspection(): Promise<void> {
    if (this.submitting()) {
      return;
    }
    this.submitting.set(true);
    try {
      await this.trySaveInspection();
    } catch (error) {
      if (this.destroyed) {
        return;
      }
      this.nbToastrService.danger('Rundgang konnte nicht gespeichert werden.', 'Rundgang');
      throw error;
    } finally {
      this.submitting.set(false);
    }
  }

  private async trySaveInspection(): Promise<void> {
    if (this.hasAnyPendingAttachments) {
      this.nbToastrService.warning('Bilder werden noch hochgeladen. Bitte warten Sie einen Moment.', 'Bitte warten');
      return;
    }

    this.firstSubmit = true;
    this.formWrapper.markAllAsTouched();
    this.formWrapper.form()?.updateValueAndValidity();

    if (!this.formWrapper.form()?.valid) {
      this.nbToastrService.danger('Rundgang kann nicht gespeichert werden, es gibt noch Fehler.', 'Rundgang');
      return;
    }

    const formValue = await this.formWrapper.getMappedValue();

    const guidelines = this.clerkGuidelines();
    if (
      formValue.status === InspectionDetailDto.StatusEnum.Done &&
      guidelines &&
      guidelines.checklistItems.length > 0 &&
      !guidelines.checklistItems.every(item => guidelines.checkedItems.includes(item))
    ) {
      const confirmed = await firstValueFrom(
        this.nbDialogService.open(ConfirmDialogComponent, {
          context: {
            header: 'Checkliste unvollständig',
            message: 'Nicht alle Punkte der Checkliste wurden abgehakt. Trotzdem als Abgeschlossen markieren?',
            status: 'warning',
          },
        }).onClose,
      );
      if (!confirmed) return;
    }

    await tryWithLoading(this.saving$, async () => {
      const buildingSectionImages = await Promise.all(
        this.formWrapper.form()!.controls.buildingSectionImages.controls.map(section => section.getMappedValue()),
      );

      const entries = await Promise.all(
        this.formWrapper.controls()!.inspectionEntries.controls.map(
          e => e.getMappedValue()));

      const inspectionDto: InspectionMergeDto = {
        ...formValue,
        buildingSectionImages,
        clerkReference: userToIdReference(formValue.clerk),
        technicianReference: userToIdReference(formValue.technician),
        fromTimestamp: TimeUtils.atTime(formValue.date, formValue.fromTime)?.toISOString(),
        toTimestamp: TimeUtils.atTime(formValue.date, formValue.toTime)?.toISOString(),
        sendingTimestamp: formValue.sendingDate?.toISOString(),
        inspectionEntries: entries as InspectionEntryMergeDto[],
      };

      this.inspection = await this.mergeInspectionAndWaitForJobToComplete(inspectionDto);

      // Der Notizen-Tab hängt nicht am formWrapper — eine dort noch ungespeicherte
      // Notiz muss das Haupt-Speichern mitnehmen, sonst meldet der Toast Erfolg,
      // der Redirect wirft vom Tab und die Notiz ist weg.
      const notesComponent = this.inspectionNotesComponent();
      if (notesComponent?.noteControl.dirty) {
        await notesComponent.save();
      }

      this.inspectionEntriesComponent()?.resetLoadedSections();

      this.nbToastrService.success('Rundgang wurde gespeichert.', 'Gespeichert', { icon: saveIcon });
      await this.initForm(this.inspection);
      await this.router.navigate(['inspection', this.inspection.id], { replaceUrl: true });
    });
  }

  async cancel(): Promise<void> {
    if (!this.formWrapper.dirty() || await firstValueFrom(this.nbDialogService.open(ConfirmDialogComponent).onClose)) {
      await this.initForm(this.inspection);
      this.formWrapper.disable();
      this.inspectionEntriesComponent()?.resetLoadedSections();
    }
  }

  async navigateBack(): Promise<void> {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      await this.router.navigate(['inspections']);
    }
  }

  ngOnInit(): void {
    this.authService.loggedIn$.pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.userService.findCurrentUser()),
    ).subscribe((currentUser) => {
      this.currentUser$.next(currentUser);
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeTab(tab: NbTabComponent) {
    const id = this.inspection?.id ?? 'new';
    this.location.replaceState(`/inspection/${id}/${tab.tabId}`);
  }

  async showEditOverrideDialog(): Promise<void> {
    if (await firstValueFrom(this.nbDialogService.open(ConfirmEditDialogComponent).onClose)) {
      this.lockEdit = false;
    }
  }

  async showDeleteDialog(): Promise<void> {
    if (await firstValueFrom(this.nbDialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Rundgang löschen',
        message: 'Bist du sicher, dass du den Rundgang löschen willst?',
      },
    }).onClose)) {
      await tryWithLoading(this.deleting$, async () => {
        const id = this.formWrapper?.value()?.id;
        if (!id) {
          throw new Error('Inspection id not found');
        }
        await firstValueFrom(this.inspectionService.deleteById(id));
        this.nbToastrService.success('Rundgang wurde erfolgreich gelöscht', 'Gelöscht');
        await this.router.navigate(['inspections']);
      });
    }
  }

  async showEmailDialog(inspection: InspectionDetailDto) {
    const allInspectionPdfs = this.inspectionContext.inspectionPdfs();

    if (!allInspectionPdfs) {
      return;
    }

    const requiredTypes = await firstValueFrom(this.inspectionPdfService.getEmailRequiredPdfTypes(inspection.id));
    const missingItems = [...requiredTypes]
      .filter(type => !allInspectionPdfs.some(pdf => pdf.inspectionPdfType === type))
      .map(type => defaultPdfOptions.find(option => option.type === type)?.label ?? type);

    if (missingItems.length > 0) {
      const missingItemsText = missingItems.join(' und ');

      let text: string;

      if (missingItems.length === 1) {
        text = `Es wurde noch keine ${missingItemsText} generiert!`;
      } else {
        text = `Es wurden noch keine ${missingItemsText} generiert!`;
      }

      this.nbDialogService.open(InfoDialogComponent, {
        context: {
          header: 'Fehlende Einträge',
          text: text,
        },
      });

      return;
    }

    const building = await firstValueFrom(this.buildingService.findById(inspection.building.id));

    const propertyManagerEmail =
      building.allocationData.propertyManager?.email
      ?? building.allocationData.embeddedPropertyManager?.propertyManagerEmail;

    const firstContactEmail = building.contacts.find(contact => contact.email)?.email;

    const defaultRecipient = propertyManagerEmail || firstContactEmail;
    const defaultRecipients = defaultRecipient ? [defaultRecipient] : [];

    const propertyManager = building.allocationData.propertyManager;
    const embeddedPropertyManager = building.allocationData.embeddedPropertyManager;
    const propertyManagerName = (propertyManager?.fullName
      ?? `${embeddedPropertyManager?.propertyManagerFirstName ?? ''} ${embeddedPropertyManager?.propertyManagerLastName ?? ''}`).trim();

    const seenSuggestionEmails = new Set<string>();
    const recipientSuggestions: RecipientSuggestion[] = [
      ...(propertyManagerEmail
        ? [{ email: propertyManagerEmail, display: `${propertyManagerName || 'Hausverwalter'} <${propertyManagerEmail}> — Hausverwalter` }]
        : []),
      ...building.contacts
        .filter(contact => contact.email && contact.email !== propertyManagerEmail)
        .map(contact => ({ email: contact.email!, display: `${contact.name} <${contact.email}>` })),
    ].filter(suggestion => {
      if (seenSuggestionEmails.has(suggestion.email)) {
        return false;
      }
      seenSuggestionEmails.add(suggestion.email);
      return true;
    });

    const url = joinUrl(window.origin);
    const emailContent = await tryWithLoading(this.importing$, async () =>
      await firstValueFrom(this.inspectionService.generateInspectionEmail(inspection.id, url)),
    );

    const dialogRef = this.nbDialogService.open(SendEmailComponent, {
      context: {
        title: 'E-Mail senden',
        message: 'Bist du sicher, dass du die E-Mail an die/den Empfänger senden willst?',
        emailMessage: emailContent,
        recipients: defaultRecipients,
        recipientSuggestions: recipientSuggestions,
      },
    });
    const message = await firstValueFrom(dialogRef.onClose);
    if (!message) {
      this.nbToastrService.info('Es wurde keine E-Mail versendet', 'E-Mail abgebrochen');
      return;
    }

    const sendMailDto = {
      ...message,
      recipients: [...(message.recipients ?? [])],
      cc: [...(message.cc ?? [])],
      inspectionId: inspection.id,
      emailType: EmailTypeEnum.Inspection,
    };

    if (message) {
      this.inspectionService.sendMail(sendMailDto).subscribe(async () => {
        this.inspection = await firstValueFrom(this.inspectionService.findById(inspection.id));
        await this.initForm(this.inspection);

        this.nbToastrService.success('E-Mail wurde erfolgreich versendet', 'E-Mail versendet');
      });
    }
  }

  private async mergeInspectionAndWaitForJobToComplete(inspectionMergeDto: InspectionMergeDto): Promise<InspectionDetailDto> {
    const { jobId } = await firstValueFrom(this.inspectionService.mergeAsync(inspectionMergeDto));
    const deadline = Date.now() + mergeStatusPollTimeoutMs;
    let consecutiveErrors = 0;
    let consecutiveUnknownPolls = 0;

    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, mergeStatusPollIntervalMs));
      if (this.destroyed) {
        throw new Error('Speichern abgebrochen, Seite wurde verlassen.');
      }

      let job: AsyncMergeJobObject;
      try {
        job = await firstValueFrom(this.inspectionService.mergeAsyncStatus(jobId));
        consecutiveErrors = 0;
      } catch (error) {
        consecutiveErrors++;
        if (consecutiveErrors >= maxConsecutivePollErrors) {
          throw error;
        }
        continue;
      }

      if (job.status === AsyncMergeJobObject.StatusEnum.Unknown) {
        consecutiveUnknownPolls++;
        if (consecutiveUnknownPolls >= maxConsecutiveUnknownPolls) {
          throw new Error('Der Server kennt den Speichervorgang nicht mehr. Bitte die Seite neu laden und prüfen, ob die Änderungen gespeichert wurden.');
        }
        continue;
      }
      consecutiveUnknownPolls = 0;

      if (job.status === AsyncMergeJobObject.StatusEnum.Completed) {
        return job.result! as InspectionDetailDto;
      }
      if (job.status === AsyncMergeJobObject.StatusEnum.Failed) {
        throw new Error(typeof job.error === 'string' ? job.error : 'Merge-Job fehlgeschlagen.');
      }
    }
    throw new Error('Zeitüberschreitung beim Speichern des Rundgangs.');
  }
}
